import { z } from 'zod';

import { ApiResponseError, assertOk, parseJson } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type JsonRequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

const hasHeader = (headers: Record<string, string>, name: string): boolean => {
  const needle = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === needle);
};

const DATE_ONLY_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const toLocalBoundaryIsoString = (value: string, edge: 'start' | 'end'): string | null => {
  const trimmed = value.trim();
  if (!DATE_ONLY_PARAM_PATTERN.test(trimmed)) {
    return null;
  }

  const date = new Date(`${trimmed}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  if (edge === 'end') {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }

  return date.toISOString();
};

type RangeParams = {
  from?: string;
  to?: string;
};

const normaliseDateRangeFilters = <T extends RangeParams>(params: T): T => {
  let changed = false;
  const next = { ...params };

  if (typeof params.from === 'string') {
    const normalisedFrom = toLocalBoundaryIsoString(params.from, 'start');
    if (normalisedFrom) {
      next.from = normalisedFrom;
      changed = true;
    }
  }

  if (typeof params.to === 'string') {
    const normalisedTo = toLocalBoundaryIsoString(params.to, 'end');
    if (normalisedTo) {
      next.to = normalisedTo;
      changed = true;
    }
  }

  return changed ? (next as T) : params;
};

async function requestJson<T>(
  path: string,
  schema: z.ZodType<T>,
  { method = 'GET', body, signal, headers = {} }: JsonRequestOptions = {},
): Promise<T> {
  const apiFetch = await resolveApiFetch();

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };

  if (body !== undefined && !hasHeader(requestHeaders, 'Content-Type')) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    headers: requestHeaders,
  });

  if (response.status === 204) {
    await assertOk(response);
    const parsed = schema.safeParse(undefined);
    if (!parsed.success) {
      throw new ApiResponseError('Expected JSON response', response.status);
    }
    return parsed.data;
  }

  await assertOk(response);
  return parseJson(response, schema);
}

const PaymentsTotalsSchema = z
  .object({
    count: z.number(),
    total_cents: z.number(),
    cash_cents: z.number(),
    card_cents: z.number(),
    refunds_cents: z.number(),
    tips_cents: z.number(),
    surcharge_cents: z.number(),
  })
  .passthrough();
export type PaymentsTotals = z.infer<typeof PaymentsTotalsSchema>;

const PaymentsApiPaymentSchema = z
  .object({
    id: z.string(),
    amount_cents: z.number(),
    method: z.string(),
    created_at: z.string(),
    status: z.string(),
  })
  .passthrough();
export type PaymentsApiPayment = z.infer<typeof PaymentsApiPaymentSchema>;

const PaymentsRangeSchema = z
  .object({
    from: z.string().nullable().optional(),
    to: z.string().nullable().optional(),
  })
  .passthrough();

const PaymentsResponseSchema = z
  .object({
    range: PaymentsRangeSchema.optional(),
    totals: PaymentsTotalsSchema,
    payments: z.array(PaymentsApiPaymentSchema),
  })
  .passthrough();
export type PaymentsResponse = z.infer<typeof PaymentsResponseSchema>;

const PaymentStatusSchema = z.enum(['pending', 'completed', 'failed', 'refunded', 'voided']);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

const PaymentRecordSchema = z
  .object({
    id: z.string(),
    orderId: z.string(),
    amountCents: z.number(),
    tipCents: z.number().nullable().optional(),
    method: z.string(),
    status: PaymentStatusSchema,
    direction: z.string().nullable().optional(),
    currency: z.string().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
    orderCode: z.string().nullable().optional(),
    createdAt: z.string(),
    capturedAt: z.string().nullable().optional(),
    refundedAt: z.string().nullable().optional(),
    refundReason: z.string().nullable().optional(),
    processedBy: z.string().nullable().optional(),
    customerName: z.string().nullable().optional(),
    customerRating: z.number().nullable().optional(),
    note: z.string().nullable().optional(),
    referenceId: z.string().nullable().optional(),
  })
  .passthrough();
export type PaymentRecord = z.infer<typeof PaymentRecordSchema>;

const PaymentRefundSchema = z
  .object({
    id: z.string(),
    paymentId: z.string(),
    amountCents: z.number(),
    reason: z.string().nullable(),
    note: z.string().nullable(),
    processedBy: z.string().nullable(),
    createdAt: z.string(),
  })
  .passthrough();
export type PaymentRefund = z.infer<typeof PaymentRefundSchema>;

const PaymentsSummarySchema = z
  .object({
    totalCount: z.number(),
    totalAmountCents: z.number(),
    totalTipCents: z.number(),
    completedCount: z.number(),
    completedAmountCents: z.number(),
    pendingCount: z.number(),
    pendingAmountCents: z.number(),
    failedCount: z.number(),
    refundedCount: z.number(),
    refundedAmountCents: z.number(),
    totalSurchargeCents: z.number().optional(),
  })
  .passthrough();
export type PaymentsSummary = z.infer<typeof PaymentsSummarySchema>;

const FetchPaymentsResponseSchema = z
  .object({
    ok: z.boolean().optional(),
    payments: z.array(PaymentRecordSchema).optional(),
    data: z.array(PaymentRecordSchema).optional(),
    summary: PaymentsSummarySchema.optional(),
    nextCursor: z.string().nullable().optional(),
  })
  .passthrough();
export type FetchPaymentsResponse = z.infer<typeof FetchPaymentsResponseSchema>;

const PaymentKpisSchema = z
  .object({
    window: z.object({
      from: z.string(),
      to: z.string(),
    }),
    totals: z.object({
      payments: z.number(),
      cardPayments: z.number(),
      walletPayments: z.number(),
      refundedPayments: z.number(),
      cardSurchargeCents: z.number(),
    }),
    wallets: z.object({
      eligible: z.number(),
      adoptionRate: z.number(),
      applePay: z.number(),
      googlePay: z.number(),
    }),
    authorization: z.object({
      successRate: z.number(),
      failureRate: z.number(),
      stepUpRate: z.number(),
      totals: z.object({
        succeeded: z.number(),
        failed: z.number(),
        pending: z.number(),
      }),
      declineReasons: z.array(z.object({ reason: z.string(), count: z.number() })),
    }),
    threeDS: z.object({
      stepUpCount: z.number(),
      stepUpRate: z.number(),
    }),
    refunds: z.object({
      refundCount: z.number(),
      refundAmountCents: z.number(),
      chargebackCount: z.number(),
    }),
  })
  .passthrough();
export type PaymentKpis = z.infer<typeof PaymentKpisSchema>;

const DisputeTimelineEventSchema = z
  .object({
    id: z.string(),
    from: z.string().nullable().optional(),
    to: z.string(),
    trigger: z.string(),
    actorRole: z.string().nullable(),
    reason: z.string().nullable(),
    occurredAt: z.string(),
  })
  .passthrough();

const KdsTimelineEventSchema = z
  .object({
    id: z.string(),
    eventType: z.string(),
    stationId: z.string().nullable(),
    actorRole: z.string().nullable(),
    occurredAt: z.string(),
  })
  .passthrough();

const DisputeEvidenceSchema = z
  .object({
    paymentId: z.string(),
    orderId: z.string().nullable(),
    orderCode: z.string().nullable(),
    amountCents: z.number(),
    currency: z.string(),
    referenceId: z.string().nullable(),
    totals: z
      .object({
        subtotalCents: z.number(),
        taxCents: z.number(),
        serviceCents: z.number(),
        surchargeCents: z.number(),
        tipCents: z.number(),
        totalCents: z.number(),
        currency: z.string(),
      })
      .passthrough(),
    pickup: z
      .object({
        name: z.string().nullable(),
        readyAt: z.string().nullable(),
        capturedAt: z.string().nullable(),
        scheduledFor: z.string().nullable(),
        scheduledWindowLabel: z.string().nullable(),
      })
      .passthrough(),
    orderTimeline: z.array(DisputeTimelineEventSchema),
    kdsTimeline: z.array(KdsTimelineEventSchema),
    media: z.object({
      photos: z.array(z.object({ url: z.string(), label: z.string().optional() }).passthrough()),
      signatures: z.array(z.object({ url: z.string(), label: z.string().optional() }).passthrough()),
    }),
  })
  .passthrough();
export type DisputeEvidence = z.infer<typeof DisputeEvidenceSchema>;

const ReceiptDeliverySchema = z.union([
  z.object({ type: z.literal('preview') }).passthrough(),
  z.object({ type: z.literal('email'), to: z.string() }).passthrough(),
  z
    .object({
      type: z.literal('download'),
      filename: z.string(),
      mimeType: z.string().optional(),
      data: z.string(),
      size: z.number().optional(),
    })
    .passthrough(),
]);
export type ReceiptDelivery = z.infer<typeof ReceiptDeliverySchema>;

const ReceiptResponseSchema = z
  .object({
    receipt: z.record(z.unknown()),
    delivery: ReceiptDeliverySchema.optional(),
  })
  .passthrough();
export type ReceiptResponse = z.infer<typeof ReceiptResponseSchema>;

const RefundPaymentResponseSchema = z
  .object({
    ok: z.boolean().optional(),
    payment: PaymentRecordSchema,
    refundPayment: PaymentRecordSchema.optional(),
    refund: PaymentRefundSchema,
    refunds: z.array(PaymentRefundSchema),
  })
  .passthrough();
export type RefundPaymentResponse = z.infer<typeof RefundPaymentResponseSchema>;

const ReconcilePaymentsRunSchema = z
  .object({
    id: z.string(),
    status: z.string(),
    startedAt: z.string(),
    completedAt: z.string(),
    totals: z.object({
      paymentsAmountCents: z.number(),
      ordersAmountCents: z.number(),
      differenceCents: z.number(),
    }),
    unmatchedOrders: z.array(
      z
        .object({
          id: z.string(),
          code: z.string(),
          status: z.string().nullable(),
          paymentStatus: z.string().nullable(),
          totalCents: z.number(),
        })
        .passthrough(),
    ),
    unmatchedPayments: z.array(
      z
        .object({
          id: z.string(),
          orderId: z.string().nullable(),
          status: z.string(),
          amountCents: z.number(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

const ReconcilePaymentsResponseSchema = z
  .object({
    run: ReconcilePaymentsRunSchema,
  })
  .passthrough();
export type ReconcilePaymentsResponse = z.infer<typeof ReconcilePaymentsResponseSchema>;

export interface FetchPaymentsParams {
  from?: string;
  to?: string;
  status?: PaymentStatus | 'all';
  limit?: number;
  cursor?: string;
}

export interface ExportPaymentsCsvParams {
  from?: string;
  to?: string;
  status?: PaymentStatus | 'all';
  limit?: number;
}

export interface RequestReceiptOptions {
  delivery?: 'preview' | 'email' | 'download';
  email?: string;
  note?: string;
}

export interface RefundPaymentPayload {
  amountCents?: number;
  reason?: string;
  note?: string;
}

export interface ReconcilePaymentsParams {
  from?: string;
  to?: string;
}

const EMPTY_PAYMENTS_RESPONSE: PaymentsResponse = {
  range: { from: null, to: null },
  totals: {
    count: 0,
    total_cents: 0,
    cash_cents: 0,
    card_cents: 0,
    refunds_cents: 0,
    tips_cents: 0,
    surcharge_cents: 0,
  },
  payments: [],
};

export async function getPayments(from: string, to: string, signal?: AbortSignal): Promise<PaymentsResponse> {
  const params = new URLSearchParams();
  if (from) {
    params.set('from', from);
  }
  if (to) {
    params.set('to', to);
  }
  const search = params.toString();
  const path = `/payments${search ? `?${search}` : ''}`;
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (response.status === 404) {
    return {
      ...EMPTY_PAYMENTS_RESPONSE,
      range: { from, to },
    };
  }

  await assertOk(response);
  return parseJson(response, PaymentsResponseSchema);
}

export async function fetchPayments(
  params: FetchPaymentsParams = {},
  signal?: AbortSignal,
): Promise<FetchPaymentsResponse> {
  const filters = normaliseDateRangeFilters(params);
  const query = new URLSearchParams();

  if (filters.from) {
    query.set('from', filters.from);
  }
  if (filters.to) {
    query.set('to', filters.to);
  }
  if (filters.status && filters.status !== 'all') {
    query.set('status', filters.status);
  }
  if (typeof filters.limit === 'number' && Number.isFinite(filters.limit)) {
    query.set('limit', filters.limit.toString());
  }
  if (filters.cursor) {
    query.set('cursor', filters.cursor);
  }

  const search = query.toString();
  const result = await requestJson(`/payments${search ? `?${search}` : ''}`, FetchPaymentsResponseSchema, { signal });

  if (!result.payments) {
    result.payments = result.data ?? [];
  }

  return result;
}

export async function exportPaymentsCsv(params: ExportPaymentsCsvParams = {}): Promise<Blob> {
  const filters = normaliseDateRangeFilters(params);
  const query = new URLSearchParams();

  if (filters.from) {
    query.set('from', filters.from);
  }
  if (filters.to) {
    query.set('to', filters.to);
  }
  if (filters.status && filters.status !== 'all') {
    query.set('status', filters.status);
  }
  if (typeof filters.limit === 'number' && Number.isFinite(filters.limit)) {
    query.set('limit', filters.limit.toString());
  }

  const path = `/payments/export${query.toString() ? `?${query.toString()}` : ''}`;
  const apiFetch = await resolveApiFetch();
  const response = await apiFetch(path, {
    method: 'GET',
    headers: {
      Accept: 'text/csv,application/octet-stream',
    },
  });

  await assertOk(response);
  return response.blob();
}

export async function getPaymentKpis(params: { from?: string; to?: string } = {}): Promise<PaymentKpis> {
  const filters = normaliseDateRangeFilters(params);
  const query = new URLSearchParams();
  if (filters.from) {
    query.set('from', filters.from);
  }
  if (filters.to) {
    query.set('to', filters.to);
  }
  const result = await requestJson(
    `/payments/kpis${query.toString() ? `?${query.toString()}` : ''}`,
    z
      .object({
        ok: z.boolean(),
        kpis: PaymentKpisSchema,
      })
      .passthrough(),
  );
  return result.kpis;
}

export async function getPaymentDisputeEvidence(paymentId: string): Promise<DisputeEvidence> {
  const result = await requestJson(
    `/payments/${paymentId}/dispute-evidence`,
    z
      .object({
        ok: z.boolean(),
        evidence: DisputeEvidenceSchema,
      })
      .passthrough(),
  );
  return result.evidence;
}

export async function requestPaymentReceipt(
  paymentId: string,
  options: RequestReceiptOptions = {},
  signal?: AbortSignal,
): Promise<ReceiptResponse> {
  return requestJson(
    `/payments/${paymentId}/receipt`,
    ReceiptResponseSchema,
    {
      method: 'POST',
      body: options,
      signal,
    },
  );
}

export async function refundPayment(
  paymentId: string,
  payload: RefundPaymentPayload,
  signal?: AbortSignal,
): Promise<RefundPaymentResponse> {
  return requestJson(
    `/payments/${paymentId}/refund`,
    RefundPaymentResponseSchema,
    {
      method: 'POST',
      body: payload,
      signal,
    },
  );
}

export async function reconcilePayments(
  params: ReconcilePaymentsParams = {},
  signal?: AbortSignal,
): Promise<ReconcilePaymentsResponse> {
  const payload = normaliseDateRangeFilters(params);
  return requestJson(
    '/payments/reconciliation',
    ReconcilePaymentsResponseSchema,
    {
      method: 'POST',
      body: payload,
      signal,
    },
  );
}
